import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  ledgerMappingCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { ledgerEndpoints } from "@/features/ledger/api/endpoints";
import type { ProductProviderMappingDto } from "@/features/ledger/api/dto";

/**
 * Associe un code produit à un provider avec sa configuration
 * d'intégration (`POST /ProductProviderMappings/add`) — réservé à
 * flashpay-superadmin / flashpay-admin.
 */
export const useCreateProductProviderMapping = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["ledger", "product-provider-mappings", "create"],
    mutationFn: (body: ProductProviderMappingDto) =>
      mutateData({
        url: ledgerEndpoints.productProviderMappings.add,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }) as Promise<ProductProviderMappingDto>,
    onSuccess: () => {
      return invalidateAfterMutation(queryClient, ledgerMappingCacheKeys(), {
        resetListPage: true,
      });
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCreateProductProviderMapping failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "ledger" }
      );
    },
  });
};
